import { useCurrentUser } from '@cma/app'
import {
  EllipsisText,
  Input,
  Pagination,
  Spinner,
  usePaginate
} from '@cma/common'
import { SearchIcon, ShortcutIcon } from '@cma/icons'
import {
  ChangeEvent,
  createContext,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { Avatar } from 'src/common/Avatar'
import { Badge } from 'src/common/Badge'
import { Table } from 'src/common/Table'
import { classNames, formatDate, getTimeAgo } from 'src/common/utils'
import { useDebounce } from 'usehooks-ts'
import { FilterLocations } from '../FilterLocations'
import { useAdminRoster } from './useAdminRoster'
import { useRosterPermissions } from './useRosterPermissions'

interface IAgentRosterContext {
  search: string
  paginate: ReturnType<typeof usePaginate>
  brokerageId?: string
  officeId?: string
  hasRosterPermissions: boolean
}

const AdminRosterContext = createContext<IAgentRosterContext>({
  search: '',
  brokerageId: undefined,
  officeId: undefined,
  hasRosterPermissions: true,
  paginate: {
    after: undefined,
    before: undefined,
    first: undefined,
    last: undefined,
    limit: 10,
    search: undefined,
    setAfter() {},
    setBefore() {},
    setFirst() {},
    setLast() {},
    setSearch() {}
  }
})

export default function AdminRosterContent() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  const { hasRosterPermissions } = useRosterPermissions()

  const inputRef = useRef<HTMLInputElement>(null)
  const isFocusedRef = useRef(false)

  const [locationFilter, setLocationFilter] = useState<{
    brokerageId?: string
    officeId?: string
  }>({})

  useEffect(() => {
    function onKeyPress(event: KeyboardEvent) {
      if (event.key === '/' && !isFocusedRef.current) {
        event.preventDefault()
        inputRef.current?.focus()
      }
    }
    document.body.addEventListener('keydown', onKeyPress)
    return () => {
      document.body.removeEventListener('keydown', onKeyPress)
    }
  }, [])

  const paginate = usePaginate({ limit: 10 })

  return (
    <div className="flex min-h-[318px] flex-col">
      <div className="flex items-center justify-between">
        <h2 className="helix-display-small">Roster</h2>
      </div>
      <div className="mt-4 flex items-center justify-between space-x-2">
        <FilterLocations
          onChange={(selectedLocation) => {
            setLocationFilter({
              brokerageId:
                selectedLocation?.value?.type === 'brokerage'
                  ? selectedLocation.value.id
                  : undefined,
              officeId:
                selectedLocation?.value?.type === 'office'
                  ? selectedLocation.value.id
                  : undefined
            })
          }}
        />
        <div className="w-full max-w-[29%]">
          <Input
            ref={inputRef}
            size="xxs"
            aria-label="search"
            leftIcon={<SearchIcon />}
            rightIcon={
              <ShortcutIcon className="text-gray-700" background="#F3F4F6" />
            }
            placeholder="Search agent name or email"
            value={search}
            onFocus={() => {
              if (inputRef.current) {
                inputRef.current.dataset.focused = 'true'
              }
            }}
            onBlur={() => {
              if (inputRef.current) {
                inputRef.current.dataset.focused = 'false'
              }
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value)
            }}
          />
        </div>
      </div>
      <AdminRosterContext.Provider
        value={{
          search: debouncedSearch,
          brokerageId: locationFilter.brokerageId,
          officeId: locationFilter.officeId,
          hasRosterPermissions,
          paginate
        }}>
        <Suspense fallback={<AdminRosterSkeletonTable />}>
          <AdminRosterTable />
        </Suspense>
      </AdminRosterContext.Provider>
    </div>
  )
}

function AdminRosterTable() {
  const { search, paginate, brokerageId, officeId, hasRosterPermissions } =
    useContext(AdminRosterContext)

  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: { platformClient } = {} } = useAdminRoster({
    id: currentUser?.account?.id || '',
    brokerageId: brokerageId || undefined,
    officeId: officeId || undefined,
    value: search || undefined,
    first: paginate.first,
    last: paginate.last,
    after: paginate.after,
    before: paginate.before
  })

  const agents = platformClient?.agents

  return (
    <div className="space-y-4">
      <div className="mt-4 space-y-2 overflow-auto rounded-lg bg-white shadow-raised print:border print:shadow-none">
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>Agent</Table.Header>
              <Table.Header px="compact">Last Report</Table.Header>
              <Table.Header align="center">Last Login</Table.Header>
              <Table.Header>Role</Table.Header>
              <Table.Header px="compact" />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {!!search && !agents?.edges?.length && (
              <Table.Row>
                <Table.Data colSpan={5}>
                  <div className="flex justify-center">
                    <p className="helix-body px-4 py-2">
                      There are no results for{' '}
                      <span className="helix-body--strong">"{search}"</span>
                    </p>
                  </div>
                </Table.Data>
              </Table.Row>
            )}
            {!search && !agents?.edges?.length && (
              <Table.Row>
                <Table.Data>
                  <div>0 Agents Found</div>
                </Table.Data>
              </Table.Row>
            )}
            {agents?.edges?.map((edge) => {
              if (edge?.node) {
                const agent = edge.node

                const liveUrl =
                  agent.recentReport?.guid &&
                  currentUser?.features?.hasLive &&
                  agent.recentReport.__typename === 'CmaReport'
                    ? `${process.env.VITE_APP_CMA_URL}/live/${agent.recentReport.guid}`
                    : ''

                return (
                  <Table.Row key={agent.id} highlightOnHover>
                    <Table.Data padding="compact">
                      <div className="flex h-10 w-56 min-w-[180px] max-w-[220px] items-center space-x-2">
                        <Avatar
                          src={agent.avatar}
                          name={agent.name}
                          size="sm"
                        />
                        <div className="flex max-w-[190px] flex-col">
                          <EllipsisText
                            maxWidth={160}
                            className="helix-body--strong">
                            {agent.name}
                          </EllipsisText>
                          <EllipsisText
                            maxWidth={160}
                            className="helix-small mt-1 text-gray-500">
                            {agent.email}
                          </EllipsisText>
                        </div>
                      </div>
                    </Table.Data>
                    <Table.Data padding="compact">
                      <div className="flex h-10 w-full min-w-[200px] max-w-[250px] flex-col justify-center leading-tight">
                        {!agent.recentReport ? (
                          <p className="text-gray-900">-</p>
                        ) : (
                          <>
                            <div className="flex items-end space-x-2">
                              <EllipsisText
                                maxWidth={140}
                                className="flex-2 helix-body text-gray-900">
                                {agent.recentReport.title}
                              </EllipsisText>
                            </div>
                            <div className="mt-1 flex space-x-3 text-xs">
                              <a
                                href={
                                  agent.recentReport.pdfPermalink || undefined
                                }
                                target="_blank"
                                rel="noreferrer"
                                className={classNames(
                                  'whitespace-nowrap text-brand-primary hover:underline',
                                  {
                                    'cursor-not-allowed opacity-50':
                                      !agent.recentReport.pdfPermalink
                                  }
                                )}>
                                View PDF
                              </a>
                              <a
                                href={liveUrl || undefined}
                                target="_blank"
                                rel="noreferrer"
                                className={classNames(
                                  'whitespace-nowrap text-brand-primary hover:underline',
                                  {
                                    'cursor-not-allowed opacity-50': !liveUrl
                                  }
                                )}>
                                View Live
                              </a>
                              {agent.recentReport.updatedAt && (
                                <div className="text-xs text-gray-400">
                                  {formatDate(
                                    agent.recentReport.updatedAt,
                                    'MM/dd/yyyy'
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </Table.Data>
                    <Table.Data align="center">
                      {agent.status === 'complete' && agent.lastSignInAt ? (
                        <Badge>{getTimeAgo(agent.lastSignInAt)}</Badge>
                      ) : (
                        '-'
                      )}
                    </Table.Data>
                    <Table.Data>
                      {agent?.platformRole ? (
                        <Badge variant="default">{agent.platformRole}</Badge>
                      ) : (
                        '-'
                      )}
                    </Table.Data>
                    <Table.Data padding="compact">
                      {hasRosterPermissions && (
                        <a
                          href={`${process.env.VITE_APP_CMA_URL}/switch_user/remember_user?scope_identifier=user_${agent.id}&remember=true`}
                          className="whitespace-nowrap font-semibold text-gray-600 hover:underline">
                          Log in as
                        </a>
                      )}
                    </Table.Data>
                  </Table.Row>
                )
              }
            })}
          </Table.Body>
        </Table>
      </div>
      <Pagination
        key={search}
        total={agents?.total}
        limit={paginate.limit}
        disablePrev={!agents?.pageInfo.hasPreviousPage}
        disableNext={!agents?.pageInfo.hasNextPage}
        onPrevClick={() => {
          paginate.setBefore(agents?.pageInfo.startCursor ?? undefined)
          paginate.setAfter(undefined)
          paginate.setFirst(undefined)
          paginate.setLast(paginate.limit)
        }}
        onNextClick={() => {
          paginate.setBefore(undefined)
          paginate.setAfter(agents?.pageInfo?.endCursor ?? undefined)
          paginate.setFirst(paginate.limit)
          paginate.setLast(undefined)
        }}
      />
    </div>
  )
}

function AdminRosterSkeletonTable() {
  return (
    <div className="mt-4 space-y-2 overflow-auto rounded-lg bg-white shadow-raised print:border print:shadow-none">
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Header px="compact">
              <div className="min-w-[260px]">Agent</div>
            </Table.Header>
            <Table.Header px="compact">
              <div className="min-w-[240px]">Last Report</div>
            </Table.Header>
            <Table.Header>
              <div className="whitespace-nowrap">Last Login</div>
            </Table.Header>
            <Table.Header>Role</Table.Header>
            <Table.Header></Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          <Table.Row>
            <Table.Data colSpan={5}>
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="h-5 w-5">
                  <Spinner />
                </div>
                <div>Loading Agents</div>
              </div>
            </Table.Data>
          </Table.Row>
        </Table.Body>
      </Table>
    </div>
  )
}
